import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageOff } from 'lucide-react';

export interface AttachmentImageProps {
  src: string;
  /** Described, never empty: a photo nobody can see is exactly when the words matter. */
  alt: string;
  /**
   * Intrinsic pixel size, when the server measured it. Null on everything uploaded
   * before it started to — passed through as "unknown" rather than guessed, because a
   * wrong ratio shifts the layout these attributes exist to hold still.
   */
  width?: number | null;
  height?: number | null;
  className?: string;
}

/**
 * One attachment, rendered for a cheap handset on a bad connection.
 *
 * There is no thumbnail endpoint: every one of these is a full-resolution fetch over
 * whatever signal a basement has, which is why `loading="lazy"` is not a nicety here —
 * a gallery of eight photos would otherwise open eight parallel downloads before the
 * reader has scrolled to any of them.
 *
 * A failed fetch falls back to a quiet line of text rather than the browser's broken-image
 * icon. On a phone in a plant room the icon reads as a bug in the app; the words read as
 * what actually happened, and they do it without shouting.
 */
export function AttachmentImage({ src, alt, width, height, className }: AttachmentImageProps) {
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <span
        className={`flex items-center justify-center gap-1.5 bg-slate-900 p-2 text-xs text-slate-500 ${className ?? ''}`}
      >
        <ImageOff className="size-4 shrink-0" aria-hidden="true" />
        {t('attachment.unavailable')}
      </span>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      width={width ?? undefined}
      height={height ?? undefined}
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
      className={className}
    />
  );
}
